// ---------------------------------------------------------------------------
// AI-drafting helper for SMART IEP goals.
//
// Two ways to reach an LLM, in order of preference:
//
//   1. Shared Supabase Edge Function `draft-goal` (preferred).
//      The single AI API key (Gemini OR OpenAI) lives in a Supabase secret
//      and never leaves the server. Teachers just click "Draft with AI"
//      and it works — zero setup per teacher, zero exposed keys.
//
//   2. Bring your own key (fallback).
//      A teacher can save their own AI API key in this browser's
//      localStorage. We auto-detect the provider from the key format:
//         * Google AI Studio (Gemini):  AIzaSy...   — recommended, free tier
//         * OpenAI:                      sk-...
//      Used when the Edge Function isn't deployed yet, or when the shared
//      account is rate-limited, or when a power user prefers to pay
//      their own way.
//
// The umbrella `draftGoal()` tries (1) first, and if the server says "AI
// isn't set up on this project yet" (HTTP 501 from the function) it
// automatically falls back to (2) — provided the teacher has a key on
// file. Everything else (auth failures, rate limits, upstream errors) is
// surfaced to the UI as a normal error the caller can render.
//
// The BYO key is stored in localStorage under `edutracker.ai_api_key.v1`.
// It's never written to Supabase, GitHub, or any other server.
// ---------------------------------------------------------------------------

import { supabase, isSupabaseConfigured } from './supabase';

// Migration note: older builds used `edutracker.openai_api_key.v1`. We keep
// reading that key on load so nobody has to re-paste their key after this
// PR ships; writes go to the new provider-agnostic name.
const KEY_STORAGE = 'edutracker.ai_api_key.v1';
const LEGACY_KEY_STORAGE = 'edutracker.openai_api_key.v1';
const DEFAULT_OPENAI_MODEL = 'gpt-4o-mini';
// Google has retired both gemini-1.5-flash (early 2025) and gemini-2.5-flash
// (mid-2026) for new users. gemini-3.1-flash-lite is the current recommended
// free-tier route per ai.google.dev/gemini-api/docs/gemini-3. Advanced users
// on the BYO path can edit this file if Google deprecates again; the shared
// Edge Function has automatic multi-model fallback so the primary path is
// resilient without a code change.
const DEFAULT_GEMINI_MODEL = 'gemini-3.1-flash-lite';

// The Supabase Edge Function to invoke for AI drafting.
//
// Defaults to `draft-goal` (the canonical name). Admins whose Supabase
// dashboard auto-generated a random slug on first deploy (like
// `quick-processor` or `smooth-endpoint`) can point us at that slug by
// setting `VITE_AI_FUNCTION_NAME` in Cloudflare Pages → Environment
// variables. Change it later without a code change if you rename or
// re-deploy under a proper slug.
const EDGE_FUNCTION_NAME =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    typeof import.meta.env.VITE_AI_FUNCTION_NAME === 'string' &&
    import.meta.env.VITE_AI_FUNCTION_NAME.trim()) ||
  'draft-goal';

// Historical note: we used to cache "shared AI is unavailable" at the
// module level for a whole page-load, to save one round-trip on repeat
// clicks. That caused a nasty bug — if a single early request failed
// (e.g. Edge Function still deploying, transient network hiccup, admin
// mid-rotation of the secret), every subsequent click in the same tab
// would skip the shared drafter and go straight to BYO fallback, forever.
// Users had to know to reload the page to recover, which teachers won't.
//
// We now re-try the shared drafter on every request. The extra round-trip
// is worth ~200ms; being stuck in a broken state for the rest of the
// session is worth an angry teacher.

// ---------- Key management (BYO fallback) --------------------------------

// detectProvider — figure out which upstream API a raw key targets.
//
//   * Gemini (Google AI Studio) keys look like AIzaSy... (39 chars starting
//     with "AIzaSy") — the standard Google API key format.
//   * OpenAI keys start with sk- (sk-, sk-proj-, sk-svcacct-, sk-admin-…).
//   * Anything else we default to OpenAI-compatible so custom endpoints
//     keep working, but that path isn't officially supported.
export function detectProvider(key) {
  if (!key || typeof key !== 'string') return null;
  const trimmed = key.trim();
  if (!trimmed) return null;
  if (/^AIza[0-9A-Za-z_-]{20,}$/.test(trimmed)) return 'gemini';
  if (/^sk-/i.test(trimmed)) return 'openai';
  return 'openai';
}

function readStoredKey() {
  if (typeof window === 'undefined') return '';
  try {
    const current = window.localStorage.getItem(KEY_STORAGE);
    if (current && current.trim()) return current;
    // Migrate legacy openai-only key one time so existing users don't have
    // to re-paste after this PR ships.
    const legacy = window.localStorage.getItem(LEGACY_KEY_STORAGE);
    if (legacy && legacy.trim()) {
      try {
        window.localStorage.setItem(KEY_STORAGE, legacy);
      } catch {
        // ignore — read still returns the legacy value
      }
      return legacy;
    }
    return '';
  } catch {
    return '';
  }
}

export function hasApiKey() {
  return Boolean(readStoredKey());
}

export function getApiKey() {
  return readStoredKey();
}

export function setApiKey(value) {
  if (typeof window === 'undefined') return;
  try {
    if (value && value.trim()) {
      window.localStorage.setItem(KEY_STORAGE, value.trim());
    } else {
      window.localStorage.removeItem(KEY_STORAGE);
      window.localStorage.removeItem(LEGACY_KEY_STORAGE);
    }
  } catch {
    // Private-mode Safari etc. — nothing to do; caller will just see
    // "no key on file" on the next check.
  }
}

// ---------- Shared Edge Function path ------------------------------------

// True if the app is configured to reach a Supabase-hosted drafter at
// all. This does NOT probe the server — it just tells the UI whether
// there's any point in trying. The umbrella `draftGoal()` re-tries the
// shared drafter on every call and surfaces the real server error to
// the caller when it fails, so callers don't need a "was it available
// last time?" cache.
export function isSharedAiAvailable() {
  if (!isSupabaseConfigured() || !supabase) return false;
  return true;
}

async function draftGoalViaEdge(prompt, hints = {}) {
  if (!isSupabaseConfigured() || !supabase) {
    const err = new Error(
      'Supabase is not configured, so shared AI drafting is unavailable in this build. Ask your admin to set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
    );
    err.__edgeUnavailable = true;
    throw err;
  }

  const { data, error } = await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
    body: { prompt, hints },
  });

  // supabase-js surfaces both non-2xx statuses and network errors in `error`.
  // A missing function comes back as a FunctionsHttpError with status 404.
  if (error) {
    const status = error?.status ?? error?.context?.status;
    // Try to pull a real message out of the response body — supabase-js
    // sometimes rolls the body up into error.context.
    let message = error?.message || 'AI drafting failed.';
    try {
      const ctx = error?.context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        if (parsed?.error) message = parsed.error;
      } else if (ctx?.body && typeof ctx.body === 'string') {
        try {
          const parsed = JSON.parse(ctx.body);
          if (parsed?.error) message = parsed.error;
        } catch {
          message = ctx.body || message;
        }
      }
    } catch {
      // ignore — fall through with the generic message
    }

    // 501 = admin hasn't set an AI provider secret yet.
    // 404 = the Edge Function isn't deployed at this name.
    // Both mean "the shared drafter isn't available" — mark the error
    // so the umbrella caller can decide whether to fall back to BYO.
    // We do NOT cache the state; every request retries so a mid-session
    // fix by the admin takes effect immediately.
    if (status === 501 || status === 404) {
      const err = new Error(message);
      err.__edgeUnavailable = true;
      err.status = status;
      throw err;
    }

    const err = new Error(message);
    err.status = status;
    throw err;
  }

  const draft = data?.draft;
  if (!draft) {
    throw new Error('AI drafter returned an empty response. Try again.');
  }
  return {
    goalArea: draft.goalArea || hints?.goalArea || 'other',
    specific: draft.specific || '',
    measurable: draft.measurable || '',
    baseline: draft.baseline || '',
    target: draft.target || '',
    masteryCriteria: draft.masteryCriteria || '',
  };
}

// ---------- BYO key path (fallback) --------------------------------------

const SYSTEM_PROMPT = `You are an IEP goal-writing assistant for a special-education teacher.
You produce SMART goals (Specific, Measurable, Achievable, Relevant, Time-bound)
that map to a fixed schema. You never claim to be a licensed professional.
You never invent student PII. If the teacher's description is thin, you
write a reasonable draft and put a bracketed placeholder like
"[teacher: add baseline]" in the field that needs more info.

Always return a single JSON object with EXACTLY these keys:
  {
    "goalArea": one of ["academic","behavioral","social-emotional","communication","functional","motor","transition","other"],
    "specific": string,
    "measurable": string,
    "baseline": string,
    "target": string,
    "masteryCriteria": string
  }

Style rules:
- "specific" is one sentence, observable and measurable ("student will…").
- "measurable" describes HOW you'd measure it (frequency, percent, duration).
- "baseline" describes the current level. If unknown, write "[teacher: add current baseline]".
- "target" is the target level of performance.
- "masteryCriteria" is like "3 consecutive weeks at 80%".
Do not include markdown, prose, or explanations — return the JSON object only.`;

function buildUserPrompt(description, hints = {}) {
  const parts = [`Teacher description: ${description}`];
  if (hints.grade) parts.push(`Grade: ${hints.grade}`);
  if (hints.goalArea && hints.goalArea !== 'other') {
    parts.push(`Preferred goal area (map to one of the allowed values): ${hints.goalArea}`);
  }
  parts.push('Return JSON only.');
  return parts.join('\n');
}

function normalizeDraft(parsed, hints = {}) {
  const obj = parsed && typeof parsed === 'object' ? parsed : {};
  return {
    goalArea: obj.goalArea || hints.goalArea || 'other',
    specific: obj.specific || '',
    measurable: obj.measurable || '',
    baseline: obj.baseline || '',
    target: obj.target || '',
    masteryCriteria: obj.masteryCriteria || '',
  };
}

async function draftViaOwnOpenAI(key, description, hints) {
  let response;
  try {
    response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${key}`,
      },
      body: JSON.stringify({
        model: DEFAULT_OPENAI_MODEL,
        temperature: 0.4,
        response_format: { type: 'json_object' },
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user', content: buildUserPrompt(description, hints) },
        ],
      }),
    });
  } catch (networkErr) {
    throw new Error(
      `Couldn't reach OpenAI: ${networkErr?.message || 'network error'}. Check your internet connection.`,
    );
  }

  if (!response.ok) {
    let body = '';
    try {
      const errJson = await response.json();
      body = errJson?.error?.message || JSON.stringify(errJson);
    } catch {
      try {
        body = await response.text();
      } catch {
        body = '';
      }
    }
    if (response.status === 401) {
      throw new Error(
        `OpenAI rejected the API key (401). Double-check that you pasted it correctly and that it isn't disabled.\n\nDetails: ${body || 'no details from OpenAI'}`,
      );
    }
    if (response.status === 429) {
      throw new Error(
        `OpenAI is rate-limiting or out of quota on your account (429).\n\nDetails: ${body || 'no details from OpenAI'}`,
      );
    }
    throw new Error(
      `OpenAI returned an error (${response.status}).\n\nDetails: ${body || 'no details from OpenAI'}`,
    );
  }

  let json;
  try {
    json = await response.json();
  } catch {
    throw new Error('OpenAI returned a response we could not parse.');
  }
  const raw = json?.choices?.[0]?.message?.content;
  if (!raw) {
    throw new Error('OpenAI returned an empty draft. Try again with a slightly longer description.');
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(
      'OpenAI returned text instead of JSON. Try again — usually just retrying fixes it.',
    );
  }
  return normalizeDraft(parsed, hints);
}

async function draftViaOwnGemini(key, description, hints) {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(DEFAULT_GEMINI_MODEL)}:generateContent?key=${encodeURIComponent(key)}`;
  const body = {
    systemInstruction: {
      role: 'user',
      parts: [{ text: SYSTEM_PROMPT }],
    },
    contents: [
      {
        role: 'user',
        parts: [{ text: buildUserPrompt(description, hints) }],
      },
    ],
    generationConfig: {
      temperature: 0.4,
      responseMimeType: 'application/json',
    },
  };

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    throw new Error(
      `Couldn't reach Google Gemini: ${networkErr?.message || 'network error'}. Check your internet connection.`,
    );
  }

  if (!response.ok) {
    let details = '';
    try {
      const errJson = await response.json();
      details = errJson?.error?.message || JSON.stringify(errJson);
    } catch {
      try {
        details = await response.text();
      } catch {
        details = '';
      }
    }
    if (response.status === 400 && details.toLowerCase().includes('api key')) {
      throw new Error(
        `Gemini rejected the API key. Double-check that you pasted it correctly and that the "Generative Language API" is enabled for your Google project.\n\nDetails: ${details || 'no details from Google'}`,
      );
    }
    if (response.status === 401 || response.status === 403) {
      throw new Error(
        `Gemini rejected the API key (${response.status}). Double-check that you pasted it correctly and that the key isn't restricted.\n\nDetails: ${details || 'no details from Google'}`,
      );
    }
    if (response.status === 429) {
      throw new Error(
        `Gemini is rate-limiting or out of quota on your account (429). The free tier is 15 requests/min and 1,500/day per project.\n\nDetails: ${details || 'no details from Google'}`,
      );
    }
    throw new Error(
      `Gemini returned an error (${response.status}).\n\nDetails: ${details || 'no details from Google'}`,
    );
  }

  let json;
  try {
    json = await response.json();
  } catch {
    throw new Error('Gemini returned a response we could not parse.');
  }
  const parts = json?.candidates?.[0]?.content?.parts;
  const raw = Array.isArray(parts) && parts.length > 0
    ? parts.map((p) => p?.text || '').join('')
    : '';
  if (!raw) {
    throw new Error('Gemini returned an empty draft. Try again with a slightly longer description.');
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(
      'Gemini returned text instead of JSON. Try again — usually just retrying fixes it.',
    );
  }
  return normalizeDraft(parsed, hints);
}

async function draftGoalViaOwnKey(description, hints = {}) {
  const key = getApiKey();
  if (!key) {
    throw new Error(
      'No AI API key on file. Open the AI panel and paste your own key (Gemini or OpenAI), or ask your admin to enable shared AI drafting.',
    );
  }
  const provider = detectProvider(key);
  if (provider === 'gemini') {
    return draftViaOwnGemini(key, description, hints);
  }
  return draftViaOwnOpenAI(key, description, hints);
}

// ---------- Umbrella entrypoint ------------------------------------------

// draftGoal — the one function callers should use.
//
// Preference order:
//   1. Shared Supabase Edge Function (zero teacher setup).
//   2. Teacher's own OpenAI key from localStorage (BYO fallback).
//
// Options:
//   * options.preferOwnKey   — if true, skip the shared function entirely.
//     Useful for a "Use my own key instead" toggle in the UI.
//
// The returned object always has the six SMART fields
// (goalArea, specific, measurable, baseline, target, masteryCriteria)
// so the caller can prefill the form uniformly. Errors bubble up as
// standard Error objects with a display-ready `message`.
export async function draftGoal(description, hints = {}, options = {}) {
  const trimmed = String(description || '').trim();
  if (!trimmed) {
    throw new Error('Describe what you want the goal to cover, in your own words.');
  }

  if (options.preferOwnKey) {
    return draftGoalViaOwnKey(trimmed, hints);
  }

  if (isSharedAiAvailable()) {
    try {
      return await draftGoalViaEdge(trimmed, hints);
    } catch (err) {
      // Only auto-fall-back on "shared AI isn't set up" — everything
      // else (auth, rate limit, OpenAI errors, network) surfaces to the
      // UI so the teacher knows what happened.
      if (err?.__edgeUnavailable && hasApiKey()) {
        return draftGoalViaOwnKey(trimmed, hints);
      }
      throw err;
    }
  }

  // Supabase not configured at all — the only option is BYO.
  return draftGoalViaOwnKey(trimmed, hints);
}

// Back-compat named export (older callers imported `draftGoalWithAI`).
export const draftGoalWithAI = draftGoal;
